import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import moment from "moment-timezone";
import { InfoIcon, Inbox } from "lucide-react";
import { AccordionItem, Accordion, AccordionTrigger, AccordionContent } from "components/ui/accordion";
import YearPicker from "components/shared/inputs/YearPicker";
import InfoDialog from "features/TraxxionProfile/sections/History/TimeOff/InfoDialog";
import HistoryContent from "features/TraxxionProfile/sections/History/TimeOff/HistoryContent";
import useApi from "utils/api";
import { useVisible } from "utils/hooks/useVisible";
import { cn } from "lib/utils";

function TimeOff({ user }: any) {
    const { t } = useTranslation();
    const { visible, selected, open, close } = useVisible();
    const { authGet } = useApi();
    const [year, setYear] = useState(moment().year());

    const { data, isLoading } = useQuery({
        queryKey: [`/users/${user?.id}/time-off`, { year }],
        queryFn: ({ queryKey }) => {
            const [url, params] = queryKey;
            return authGet(url as string, { params });
        },
        enabled: !!user?.id,
    });

    const plans: any[] = data?.result || [];

    return (
        <div className="tw-space-y-3">
            <div className="tw-flex tw-justify-end">
                <YearPicker value={year} onChange={setYear} />
            </div>

            {!isLoading && plans.length === 0 ? (
                <div className="tw-flex tw-flex-col tw-text-neutral-500 tw-justify-center tw-items-center tw-py-8 tw-border tw-border-dashed tw-rounded-lg">
                    <Inbox />
                    <p className="tw-text-muted-foreground">{t("no-data")}</p>
                </div>
            ) : (
                <Accordion type="single" collapsible className="tw-w-full tw-space-y-2">
                    {plans.map((plan) => (
                        <AccordionItem key={plan.id} value={String(plan.id)} className="tw-border tw-rounded-xl tw-overflow-hidden">
                            <AccordionTrigger className="tw-px-5 tw-py-4 hover:tw-no-underline hover:tw-bg-muted/30 tw-transition-colors tw-cursor-pointer tw-items-center">
                                <div className="tw-flex tw-items-center tw-gap-7 tw-w-full tw-mr-3 tw-flex-wrap">
                                    <div>
                                        <p className="tw-text-sm tw-font-semibold">{plan.plan.code}</p>
                                        <p className="tw-text-xs tw-text-muted-foreground">{plan.plan.description}</p>
                                    </div>

                                    <div className="tw-flex tw-flex-wrap tw-gap-x-6 tw-gap-y-2">
                                        {[
                                            { label: t("initial-balance"), value: plan.bucket.initial },
                                            { label: t("added"), value: plan.bucket.updates },
                                            { label: t("accrued"), value: plan.bucket.earned },
                                            { label: t("taken"), value: plan.bucket.consumed },
                                            { label: t("total"), value: plan.bucket.total },
                                        ].map(({ label, value }) => (
                                            <div key={label} className="tw-flex tw-flex-col">
                                                <span className="tw-text-xs tw-text-muted-foreground">{label}</span>
                                                <span className="tw-text-sm tw-font-medium tw-tabular-nums">
                                                    {Number(value || 0).toFixed(2)}
                                                </span>
                                            </div>
                                        ))}

                                        <div className="tw-flex tw-flex-col">
                                            <span className="tw-text-xs tw-text-muted-foreground">{t("available")}</span>
                                            <span className="tw-text-sm tw-font-semibold tw-tabular-nums tw-text-green-700">
                                                {Number(plan.bucket.available || 0).toFixed(2)}
                                            </span>
                                        </div>

                                        <div className="tw-flex tw-flex-col">
                                            <span className="tw-text-xs tw-text-muted-foreground">{t("pending")}</span>
                                            <span className="tw-flex tw-items-center tw-gap-1.5">
                                                <span
                                                    className={cn(
                                                        Number(plan.bucket.pending || 0) > 0
                                                            ? "tw-text-sm tw-font-medium tw-tabular-nums tw-text-orange-600"
                                                            : "tw-text-sm tw-font-medium tw-tabular-nums"
                                                    )}
                                                >
                                                    {Number(plan.bucket.pending || 0).toFixed(2)}
                                                </span>
                                                {plan.pendingItems.length > 0 && (
                                                    <InfoIcon
                                                        className="tw-cursor-pointer tw-w-3.5 tw-h-3.5 tw-text-orange-500"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            open(plan.pendingItems);
                                                        }}
                                                    />
                                                )}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </AccordionTrigger>
                            <AccordionContent className="tw-border-t tw-p-0">
                                <HistoryContent plan={plan} loading={isLoading} />
                            </AccordionContent>
                        </AccordionItem>
                    ))}
                </Accordion>
            )}

            <InfoDialog open={visible} close={close} values={selected} />
        </div>
    );
}

export default TimeOff;
